/**
 * Bausteine der Projektprotokoll-Vorlagen (ADR 0084).
 *
 * Getrennt von den Vorlagen selbst (`project-protocol.templates.ts`), damit die
 * Typen ohne den umfangreichen Klauseltext importiert werden können.
 *
 * **Jeder sichtbare Text ist zweisprachig.** Die Papiervorlagen existierten als
 * getrennte DE-/EN-Word-Dateien und sind inhaltlich auseinandergelaufen (ADR
 * 0084, Kontext 3). Ein `Bilingual` erzwingt, dass eine Änderung beide Fassungen
 * berührt.
 */

/** Text in beiden Belegsprachen. Bei inhaltlichen Abweichungen gilt `de` (Tech-Lead 2026-08-06). */
export interface Bilingual {
  de: string;
  en: string;
}

export const PROTOCOL_LANGUAGES = ['DE', 'EN'] as const;
export type ProtocolLanguage = (typeof PROTOCOL_LANGUAGES)[number];

/** Text in der Protokollsprache. */
export function pickText(text: Bilingual, language: ProtocolLanguage): string {
  return language === 'EN' ? text.en : text.de;
}

export const PROTOCOL_TYPES = [
  'PLANFREIGABE',
  'BAUSTELLENVORBEREITUNG',
  'PROBELAUF',
  'ABNAHME',
] as const;
export type ProtocolType = (typeof PROTOCOL_TYPES)[number];

export const PROTOCOL_STATUSES = ['ENTWURF', 'VERSENDET', 'UNTERSCHRIEBEN', 'VERWORFEN'] as const;
export type ProtocolStatus = (typeof PROTOCOL_STATUSES)[number];

/** Wie ein Feld erfasst und im PDF dargestellt wird (Muster `ReportFieldType`, ADR 0070). */
export type ProtocolFieldType = 'text' | 'longtext' | 'number' | 'date' | 'bool' | 'enum';

export interface ProtocolFieldOption {
  value: string;
  label: Bilingual;
}

/**
 * Woher der Vorschlagswert beim Anlegen kommt. Bewusst eine geschlossene Liste
 * statt freier Pfade: Der Server löst sie in `resolvePrefill` auf, und ein
 * Tippfehler soll beim Übersetzen auffallen, nicht als stilles `undefined` im
 * Formular landen.
 *
 * Vorbelegung ≠ Kopie: Der Wert landet einmal im Formular und ist danach frei
 * änderbar. Eingefroren wird erst beim Unterschreiben (ADR 0084 §5).
 */
export type ProtocolPrefill =
  | 'project.customerName'
  | 'project.name'
  | 'project.code'
  | 'project.location'
  | 'project.contactName'
  | 'project.contactPhone'
  | 'project.deliveryContactName'
  | 'project.deliveryTimeNote'
  | 'project.planApprovedAt'
  | 'legalEntity.name'
  | 'assembly.period'
  | 'assembly.supervisorName'
  | 'assembly.supervisorPhone'
  | 'assembly.technicianNames';

export interface ProtocolField {
  /** Schlüssel im JSONB-Feld `data`. */
  key: string;
  label: Bilingual;
  type: ProtocolFieldType;
  /** Nur bei `enum`. */
  options?: readonly ProtocolFieldOption[];
  /** Einheit hinter dem Eingabefeld, z. B. „Jahre". */
  unit?: Bilingual;
  /** Hilfetext unter dem Feld — Erklärung, keine Vorbelegung. */
  hint?: Bilingual;
  /** Vorschlagswert beim Anlegen (siehe `ProtocolPrefill`). */
  prefill?: ProtocolPrefill;
  /** Über die ganze Breite darstellen (Langtexte, Teilnehmerlisten). */
  isWide?: boolean;
  /**
   * Wird gerechnet, nicht getippt (z. B. Gewährleistungsende aus Übergabedatum +
   * Frist). Im Formular schreibgeschützt, im PDF wie jeder andere Wert.
   */
  isDerived?: boolean;
}

export interface ProtocolSection {
  key: string;
  title: Bilingual;
  description?: Bilingual;
  fields: readonly ProtocolField[];
  /**
   * Nur zeigen/drucken, wenn dieser optionale Block angewählt ist
   * (`ProjectProtocol.optionalBlocks`). Siehe ADR 0084 §1.
   */
  optionalBlock?: string;
}

/** Ergebnis eines Prüfpunkts der Probelauf-Checkliste. */
export const CHECK_RESULTS = ['ERFUELLT', 'MANGEL', 'NICHT_ZUTREFFEND'] as const;
export type CheckResult = (typeof CHECK_RESULTS)[number];

export const CHECK_RESULT_LABELS: Record<CheckResult, Bilingual> = {
  ERFUELLT: { de: 'Erfüllt', en: 'Fulfilled' },
  MANGEL: { de: 'Mangel', en: 'Defect' },
  NICHT_ZUTREFFEND: { de: 'n. z.', en: 'n/a' },
};

export interface ProtocolCheckItemDef {
  /** Stabiler Schlüssel — die Zeile in der DB hängt daran, NICHT am Text. */
  key: string;
  text: Bilingual;
}

export interface ProtocolCheckGroup {
  key: string;
  title: Bilingual;
  items: readonly ProtocolCheckItemDef[];
}

/**
 * Unveränderlicher Klauseltext (die nummerierten Punkte und Hinweisblöcke der
 * Papiervorlagen). Wird gedruckt, nicht ausgefüllt — deshalb kein Feld.
 */
export interface ProtocolClause {
  key: string;
  /** Überschrift des Blocks; ohne Angabe läuft der Text unter dem vorherigen weiter. */
  title?: Bilingual;
  /** Absätze; führendes „◼ " markiert einen Aufzählungspunkt. */
  paragraphs: readonly Bilingual[];
  /** Nummerierter Punkt der Vorlage (1., 2., …) — nur zur Anzeige. */
  ordinal?: number;
  /** Hervorgehoben drucken (ACHTUNG/HINWEIS-Kästen der Vorlagen). */
  emphasis?: 'attention' | 'note';
  optionalBlock?: string;
}

/** Unterschriftszeile im PDF und im Signatur-Dialog. */
export interface ProtocolSignatureRole {
  value: string;
  label: Bilingual;
  /** Wie viele Personen dieser Rolle höchstens unterschreiben (Abnahme: 3 Kunden). */
  maxCount: number;
}

/** Abwählbarer Block einer Vorlage (ADR 0084 §1). */
export interface ProtocolOptionalBlock {
  key: string;
  label: Bilingual;
  /** Standardmäßig angehakt? */
  isDefaultOn: boolean;
}

export interface ProtocolTemplate {
  type: ProtocolType;
  /** Nummer des gelenkten QM-Dokuments, z. B. „2.3.2". */
  qmNumber: string;
  /** Revision der Vorlage — wird je Protokoll gespeichert und im PDF gedruckt. */
  templateRevision: string;
  title: Bilingual;
  /** Einleitungssatz unter dem Titel. */
  intro?: Bilingual;
  sections: readonly ProtocolSection[];
  checkGroups?: readonly ProtocolCheckGroup[];
  clauses?: readonly ProtocolClause[];
  optionalBlocks?: readonly ProtocolOptionalBlock[];
  signatureRoles: readonly ProtocolSignatureRole[];
  /** Braucht das Protokoll einen Montageeinsatz (Termin, Supervisor, Mängel)? */
  needsAssemblyTask: boolean;
}
